import Modal from "components/modal/Modal";
import { useEffect, useMemo, useState } from "react";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./documents.scss";
import { Button } from "components/ui/button/Button";
import FileDetails, { FileErrorBanner, FileUploadedBy, UploaderAccessOnly } from "components/file-details/FilesDetails";
import Card from "components/card/Card";
import useDesktopChecker from "hooks/useDesktopChecker";
import FileUploader from "components/file-uploader/FileUploader";
import { useSetStatusFlagOfNotificationMutation } from "pages/authentication/userService";
import {
  RetrieveDocumentsResponseDto,
  dashboardApi,
  useGetDashboardItemsQuery,
  useRetrieveDocumentsQuery,
  useRetrieveDocumentsForDealApplicationQuery,
  useUpdateDocumentIdsMutation,
  useRetrievePalContentQuery,
  RetrievePalResponseDto,
  useRetrieveNeedsQuery,
  useViewedDocumentsLogMutation,
  useDownloadedDocumentsLogMutation,
  RetrieveNeedDto,
  Document,
  DocumentStatus,
} from "pages/dashboard/home/dashboardService";
import { format } from "date-fns";
import { useSelector, useDispatch } from "react-redux";
import {
  getCurrentUserName,
  getCurrentUserApplicationId,
  setLogout,
  getDealApplicationId,
  getNhDealApplicationId,
  getLoanId,
  setIsInitialDocUploaded,
  getIsInitialDocUploaded,
  getCurrentUserId,
  getApplicationRoleType
} from "features/authSlice";
import { useNavigate } from "react-router";
import Overlay from "components/ui/loading-spinners/Overlay/Overlay";
import { showDocuments } from "utilities/UserversionFeatures";
import { Funded, PalInstanceQueryString, NotificationIdQueryString, LatestPal, ApplicationRoleTypes } from "utilities/Constant";
import { sortDocumentsByNaf } from "utilities/documents";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import PreApprovalPreview from "../home/PalPreview/PreApprovalPreview";
import { useSearchParams } from "react-router-dom";
import { isDocumentUploadEnabled } from "../store/dashboardSlice";

export default function Documents() {
  const navigate = useNavigate();
  if (!showDocuments()) {
    navigate("/");
  }
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const userId = useSelector(getCurrentUserId);
  const { data } = useGetDashboardItemsQuery({ dealApplicationId: nhDealApplicationId, userId: userId });
  const loanStatus = data?.loanStatus?.status;
  const isDesktop = useDesktopChecker();
  const [show, setShow] = useState(false);
  const [isPalPreview, setIsPalPreview] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [fileInfo, setFileInfo] = useState({ filePath: "", fileName: "", documentId: "" });
  const applicationId = useSelector(getCurrentUserApplicationId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const applicationRoleType = useSelector(getApplicationRoleType);
  const loanId = useSelector(getLoanId);
  const [documentsDropdown, setDocumentsDropdown] = useState({ isNafDocumentsVisible: false, isUserDocumentsVisible: false });
  const [documentsByUser, setDocumentsByUser] = useState<RetrieveDocumentsResponseDto[]>([]);
  const [needsByUser, setNeedsByUser] = useState<RetrieveNeedDto[]>([]);
  const [documentsByNaf, setDocumentsByNaf] = useState<RetrievePalResponseDto[]>([]);
  const [currentPalDocument, setCurrentPalDocument] = useState<RetrievePalResponseDto | undefined>(undefined);
  const [updateNotificationStatus] = useSetStatusFlagOfNotificationMutation();
  const {
    data: retrieveDocumentData,
    isLoading: isRetreiveDocumentLoading,
    isSuccess: isRetreiveDocumentSuccess,
  } = useRetrieveDocumentsQuery(applicationId, { skip: applicationId == "" || featureFlag?.needsListV2Enabled });
  const {
    data: retrieveNeedsData,
    isLoading: isRetrieveNeedsLoading,
    isSuccess: isRetrieveNeedsSuccess,
  } = useRetrieveNeedsQuery({ nhDealApplicationId, userId }, { skip: nhDealApplicationId == "" || !featureFlag?.needsListV2Enabled });
  const {
    data: retrieveDocumentDealApplicationData,
    isLoading: isRetreiveDocumentDealAppLoading,
    isSuccess: isRetreiveDocumentDealAppSuccess,
  } = useRetrieveDocumentsForDealApplicationQuery(dealApplicationId, {
    skip: applicationId != "" || dealApplicationId == "",
  });
  const {
    data: retrievePalData,
    isLoading: isRetreivePalLoading,
    isSuccess: isRetreivePalSuccess,
  } = useRetrievePalContentQuery(
    { nhDealApplicationId, loanId },
    {
      skip: !featureFlag?.preApprovalLetterEnabled || loanId === "",
    }
  );
  const [updateDownloadedDocuments] = useDownloadedDocumentsLogMutation();
  const [updateViewedDocuments] = useViewedDocumentsLogMutation();
  const [searchParams] = useSearchParams();
  const previewPalInstanceId = searchParams.get(PalInstanceQueryString);
  const notificationId = searchParams.get(NotificationIdQueryString)!;
  const documentsEnabled = useSelector(isDocumentUploadEnabled);
  const [isDocUploadEnabled, setIsDocUploadEnabled] = useState(true);
  // for additional dcoument need
  const additionalDocNeedId = retrieveNeedsData?.filter((need) => need.isDefault)[0]?.id || "";

  useEffect(() => {
    setIsDocUploadEnabled(documentsEnabled);
  }, [documentsEnabled]);

  useEffect(() => {
    if (applicationId && !isRetreiveDocumentLoading && isRetreiveDocumentSuccess) {
      setDocumentsByUser(retrieveDocumentData);
    } else if (!applicationId && dealApplicationId && !isRetreiveDocumentDealAppLoading && isRetreiveDocumentDealAppSuccess) {
      setDocumentsByUser(retrieveDocumentDealApplicationData);
    }
    if (dealApplicationId && !isRetrieveNeedsLoading && isRetrieveNeedsSuccess) {
      setNeedsByUser(retrieveNeedsData);
    }
  }, [
    applicationId,
    dealApplicationId,
    isRetreiveDocumentLoading,
    isRetreiveDocumentSuccess,
    isRetreiveDocumentDealAppLoading,
    isRetreiveDocumentDealAppSuccess,
    retrieveDocumentData,
    retrieveDocumentDealApplicationData,
    isRetrieveNeedsLoading,
    isRetrieveNeedsSuccess,
  ]);
  // Memoized PAL instance document lookup
  const palInstanceDocument = useMemo(() => {
    const recentPreviewPalInstanceId =
      previewPalInstanceId === LatestPal ? retrievePalData?.[0]?.palInstanceId ?? previewPalInstanceId : previewPalInstanceId;

    return retrievePalData?.find((p) => p.palInstanceId === recentPreviewPalInstanceId);
  }, [retrievePalData, previewPalInstanceId]);

  useEffect(() => {
    if (loanId && !isRetreivePalLoading && isRetreivePalSuccess && retrievePalData !== documentsByNaf) {
      setDocumentsByNaf(retrievePalData);
    }
  }, [isRetreivePalLoading, isRetreivePalSuccess, loanId]);

  useEffect(() => {
    if (palInstanceDocument) {
      setCurrentPalDocument(palInstanceDocument);
      void updateViewedDocuments({ userId, documentIds: [palInstanceDocument.palInstanceId] });
      setIsPalPreview(true);
      void updateNotificationStatus(notificationId);
      navigate("/dashboard/documents");
    }
  }, [palInstanceDocument, notificationId]);
  const toggleModal = () => setShow(!show);
  const [invokeDocumentContent, { isLoading: isDocumentLoading, isFetching: isDocumentFetching }] =
    dashboardApi.endpoints.retrieveDocumentContent.useLazyQuery();

  const handleClosePAL = () => {
    setIsPalPreview(false);
    setCurrentPalDocument(undefined);
  };
  const handleDocumentViewer = (path: string, name: string, documentRefId: string) => {
    if (documentRefId && retrievePalData && retrievePalData.findIndex((p) => p.palInstanceId == documentRefId) >= 0) {
      //It is Pal
      setCurrentPalDocument(retrievePalData.find((p) => p.palInstanceId == documentRefId));
      setIsPalPreview(true);
    } else {
      setFileInfo({ ...fileInfo, filePath: path, fileName: name, documentId: documentRefId });
      setShow(!show);
    }
  };
  const [display, setDisplay] = useState(false);
  const userName = useSelector(getCurrentUserName);
  const dispatch = useDispatch();
  const [UpdateDocumentIds] = useUpdateDocumentIdsMutation();
  const handleHide = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isDocUploadEnabled && (dealApplicationId || "") !== "" && (event.type === "click" || event.type === "mousedown")) {
      setDisplay(!display);
    }
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isDocUploadEnabled && (dealApplicationId || "") !== "" && (event.key === "Enter" || event.key === " ")) {
      setDisplay(!display);
    }
  };

  const handleByAction = async () => {
    if (display) {
      await UpdateDocumentsIds();
      setDisplay(!display);
    }
    setShowModal(!showModal);
  };
  const isInitialDocumentUploaded = useSelector(getIsInitialDocUploaded);
  const UpdateDocumentsIds = async () => {
    try {
      if (nhDealApplicationId && userId) {
        await UpdateDocumentIds({
          nhDealApplicationId,
          borrowerId: userId,
          documentUpdateDto: {
            isInitialDocumentUploaded: isInitialDocumentUploaded,
          },
        });
      }
      dispatch(setIsInitialDocUploaded(true));
    } catch (error) {
      console.error(`Error updating document id's`, error);
    }
  };
  const handleClick = () => setDisplay(!display);
  const docs = [{ uri: fileInfo.filePath, fileName: fileInfo.fileName }];
  const [fileUrl, setFileUrl] = useState<string>("");

  const getBlob = async (document: RetrieveDocumentsResponseDto) => {
    try {
      const { data, isError, error } = await invokeDocumentContent(document.fileId);
      if (isError && typeof error === "object" && "status" in error && error.status === 401) {
        dispatch(setLogout());
        navigate("/", { replace: true, state: { path: location.pathname } });
        return "";
      } else {
        if (data) {
          void updateViewedDocuments({ userId, documentIds: [document.fileId] });
          const blob = new Blob([Uint8Array.from(window.atob(data.fileContents), (c) => c.charCodeAt(0))], { type: data.contentType });

          const url = window.URL.createObjectURL(blob);
          setFileUrl(url); // Store the URL in state variable
          return url;
        } else {
          return "";
        }
      }
    } catch (error) {
      return "";
    }
  };
  const getPathUrl = (document: RetrieveDocumentsResponseDto, contentType?: string) => {
    if (document) {
      const blob = new Blob([Uint8Array.from(window.atob(document.documentPath), (c) => c.charCodeAt(0))], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      void updateViewedDocuments({ userId, documentIds: [document.fileId] });
      setFileUrl(url); // Store the URL in state variable
      return url;
    } else {
      return "";
    }
  };
  if (isRetreiveDocumentLoading || isRetreiveDocumentDealAppLoading || isRetreivePalLoading || isRetrieveNeedsLoading) {
    return <Overlay />;
  }

  if (isDocumentLoading || isDocumentFetching) {
    return <Overlay />;
  }

  return (
    <div className="documents-container">
      {isDesktop && (
        <div className="documents-container__upload-container">
          <div className="documents-container__file-upload" onClick={handleHide} onMouseDown={handleHide} onKeyDown={handleKeyDown}>
            {(dealApplicationId || "") !== "" && loanStatus !== Funded && applicationRoleType.toLowerCase() !== ApplicationRoleTypes.NonBorrowerOwner && (
              <Button title="Upload your file" icon={solid("cloud-arrow-up")} disabled={!isDocUploadEnabled} />
            )}
          </div>
          {!isDocUploadEnabled && (
            <div className="documents-container__uploads-unavailable">Document uploads are temporarily unavailable. Please try again later.</div>
          )}

          <div className="documents-container__line" />
        </div>
      )}
      {isDesktop ? (
        <div className="documents-container__header">
          <p className="documents-container__title">Provided by NAF</p>
        </div>
      ) : (
        <div>
          <div className="documents-container__file-upload" onClick={handleHide} onMouseDown={handleHide} onKeyDown={handleKeyDown}>
            {(dealApplicationId || "") !== "" && loanStatus !== Funded && (
              <Button title="Upload your file" icon={solid("cloud-arrow-up")} disabled={!isDocUploadEnabled} />
            )}
            {!isDocUploadEnabled && (
              <div className="documents-container__uploads-unavailable">Document uploads are temporarily unavailable. Please try again later.</div>
            )}
          </div>
          {!isDesktop && <div className="documents-container__line" />}
          <div>
            <div className="documents-container__title_wrapper">
              <p className="documents-container__title"> Provided by NAF</p>
              {!isDesktop && documentsByNaf.length > 0 && (
                <div
                  className="chevron-arrow"
                  tabIndex={0}
                  onClick={() => setDocumentsDropdown((prev) => ({ ...prev, isNafDocumentsVisible: !prev.isNafDocumentsVisible }))}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      setDocumentsDropdown((prev) => ({ ...prev, isNafDocumentsVisible: !prev.isNafDocumentsVisible }));
                    }
                  }}
                >
                  <FontAwesomeIcon icon={documentsDropdown.isNafDocumentsVisible ? solid("chevron-up") : solid("chevron-down")} />
                </div>
              )}
            </div>
            {!isDesktop && (
              <p className={`documents-container__count${documentsDropdown.isNafDocumentsVisible ? "-active" : ""}`}>
                {documentsByNaf.length > 0 ? documentsByNaf.length.toString() + " documents" : "No documents available"}
              </p>
            )}
          </div>
        </div>
      )}

      {display && (
        <Modal
          customBody={
            <FileUploader
              handleByAction={handleByAction}
              onClick={handleClick}
              operationType="UploadDocuments"
              conditionId={additionalDocNeedId}
              navigate={navigate}
            />
          }
          defaultHeader={false}
          defaultBody={false}
        />
      )}
      {showModal && <SuccessMessageModal handleByAction={handleByAction} />}
      <div>
        <DocumentCards
          documentsByUser={documentsByUser}
          needsByUser={needsByUser}
          documentsByNaf={sortDocumentsByNaf(documentsByNaf)}
          isDocumentsByNaf
          getPathUrl={getPathUrl}
          getBlob={getBlob}
          userName={userName}
          handleDocumentViewer={handleDocumentViewer}
          IsDropDownOn={documentsByNaf.length > 0 ? documentsDropdown.isNafDocumentsVisible : documentsDropdown.isUserDocumentsVisible}
        />
      </div>
      <div>
        <div>
          <div className="documents-container__line" />
          <div>
            <div className="documents-container__title_wrapper">
              <p className="documents-container__title"> Provided by You</p>
              {!isDesktop && documentsByUser.length > 0 && (
                <div
                  className="chevron-arrow"
                  tabIndex={0}
                  onClick={() => setDocumentsDropdown((prev) => ({ ...prev, isUserDocumentsVisible: !prev.isUserDocumentsVisible }))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setDocumentsDropdown((prev) => ({ ...prev, isUserDocumentsVisible: !prev.isUserDocumentsVisible }));
                    }
                  }}
                >
                  <FontAwesomeIcon icon={documentsDropdown.isUserDocumentsVisible ? solid("chevron-up") : solid("chevron-down")} />
                </div>
              )}
            </div>
            {!isDesktop && (
              <p className={`documents-container__count${documentsDropdown.isUserDocumentsVisible ? "-active" : ""}`}>
                {documentsByUser.length > 0 ? documentsByUser.length.toString() + " documents" : "No documents available"}
              </p>
            )}
          </div>
          <DocumentCards
            documentsByUser={documentsByUser}
            needsByUser={needsByUser}
            documentsByNaf={documentsByNaf}
            getPathUrl={getPathUrl}
            getBlob={getBlob}
            userName={userName}
            handleDocumentViewer={handleDocumentViewer}
            IsDropDownOn={documentsDropdown.isUserDocumentsVisible || documentsByUser.length === 0}
          />
        </div>
      </div>
      {show && (
        <Modal
          defaultHeader
          defaultHeaderIcon={solid("pen-nib")}
          defaultHeaderName={fileInfo.fileName}
          defaultHandleHide={toggleModal}
          defaultBody={false}
          footerContentPosition="right"
          customBody={
            <>
              {fileInfo.filePath ? (
                <DocViewer documents={docs} pluginRenderers={DocViewerRenderers} />
              ) : (
                <h1>Oops! There's a problem viewing this document.</h1>
              )}
            </>
          }
          footer={
            <>
              <Button title={"Close"} type={"round"} variant={"outline-primary"} onClick={toggleModal} />
              <a
                href={fileUrl}
                download={fileInfo.fileName}
                target="_blank"
                rel="noreferrer"
                onClick={() => updateDownloadedDocuments({ userId, documentIds: [fileInfo.documentId] })}
              >
                <Button title={"Download"} type={"round"} variant={"primary"} />
              </a>
            </>
          }
        />
      )}
      {isPalPreview && <PreApprovalPreview onClose={handleClosePAL} palDocument={currentPalDocument} />}
    </div>
  );
}

interface SuccessMessageModalProps {
  handleByAction: () => void;
}

export const SuccessMessageModal = ({ handleByAction }: SuccessMessageModalProps) => {
  const handleClick = () => {
    handleByAction();
    if (location.pathname === "/dashboard/documents") {
      window.location.reload();
    }
  };
  return (
    <div className="success-message__container">
      <Modal
        defaultBody={false}
        defaultHeader
        defaultHandleHide={handleByAction}
        defaultHeaderName="Upload Files"
        defaultHeaderIcon={solid("cloud-arrow-up")}
        customBody={
          <div>
            <p className="message__primary">Your files have been successfully uploaded!</p>
            <p className="message__secondary">You can view uploaded documents in the Documents tab</p>
            <Button title="Return To Dashboard" onClick={handleClick} />
          </div>
        }
      />
    </div>
  );
};

interface DocumentCardsProps {
  documentsByUser: RetrieveDocumentsResponseDto[];
  needsByUser: RetrieveNeedDto[];
  userName: string;
  getPathUrl: (document: RetrieveDocumentsResponseDto, contentType?: string) => string;
  getBlob: (document: RetrieveDocumentsResponseDto) => Promise<string>;
  handleDocumentViewer: (path: string, name: string, documentRefId: string) => void;
  IsDropDownOn: boolean;
  documentsByNaf: RetrievePalResponseDto[];
  isDocumentsByNaf?: boolean;
}

const DocumentCards = ({
  documentsByUser,
  needsByUser,
  documentsByNaf,
  userName,
  getPathUrl,
  getBlob,
  handleDocumentViewer,
  IsDropDownOn,
  isDocumentsByNaf = false,
}: DocumentCardsProps) => {
  const isDesktop = useDesktopChecker();
  const { data: featureFlag } = useGetFeatureFlagsQuery();

  const transformDocuments = (documents: RetrievePalResponseDto[]): RetrieveDocumentsResponseDto[] => {
    return documents.map((doc: RetrievePalResponseDto) => ({
      fileId: doc.palInstanceId,
      name: doc.documentContent?.name ?? "",
      documentPath: doc.documentContent?.fileContents ?? "",
      dateCreated: doc.createdDate ?? "",
      category: "",
    }));
  };

  const transformNeeds = (needs: RetrieveNeedDto[]): RetrieveDocumentsResponseDto[] => {
    return needs
      .flatMap((need) => need.documents)
      .map((doc: Document) => ({
        fileId: doc.id,
        name: doc.docName ?? "",
        documentPath: "",
        dateCreated: doc.uploadedDateTime ?? "",
        category: "",
        userName: `${doc.uploader.firstName} ${doc.uploader.lastName}`,
        status: doc.status,
        invalidReason: doc.invalidReason ?? "",
        detailedExplanation: doc.detailedExplanation ?? "",
        userNotified: doc.userNotified,
        isNoPreview: doc.isNoPreview ?? false, // Added isNoPreview property
      }));
  };

  let documents = null;
  if (isDocumentsByNaf) {
    documents = transformDocuments(documentsByNaf);
  } else if (featureFlag?.needsListV2Enabled) {
    documents = transformNeeds(needsByUser);
  } else {
    documents = documentsByUser;
  }

  const isDocumentMarkedInvalid = (document: RetrieveDocumentsResponseDto): boolean => {
    return (
      document.status?.toLowerCase() === DocumentStatus.Rejected.toLowerCase() &&
      !!document.invalidReason &&
      !!document.detailedExplanation &&
      !!document.userNotified
    );
  };

  const handlePreviewDocument = async (document: RetrieveDocumentsResponseDto) => {
    if (document.isNoPreview == true) {
      alert("Preview is not available for this document.");
      return;
    }
    handleDocumentViewer(
      isDocumentsByNaf
        ? getPathUrl(document, documentsByNaf.filter((doc) => doc.palInstanceId == document.fileId)[0].documentContent?.contentType)
        : await getBlob(document),
      document.name,
      document.fileId
    );
  };

  return (
    <>
      {documents.length > 0 ? (
        <div className={isDesktop || (!isDesktop && IsDropDownOn) ? "documents-container__cards-active" : "documents-container__cards"}>
          {documents.map((document) => {
            const cardClass = document.isNoPreview ? "documents-container__card no-preview" : "documents-container__card";
            return (
              <div className={cardClass} key={document.fileId} tabIndex={0}>
                <Card
                  body={
                    <FileDetails fileName={document.name} handlePreviewDocument={() => handlePreviewDocument(document)} noPreview={document.isNoPreview} />
                  }
                  footer={
                    <>
                      <FileUploadedBy
                        uploadedby={featureFlag?.needsListV2Enabled && document?.userName ? document?.userName : userName}
                        createdDate={format(new Date(document.dateCreated), "MM/dd/yyyy  hh:mma")}
                      />
                      {isDocumentMarkedInvalid(document) && <FileErrorBanner invalidReason={document.invalidReason} />}
                      {document.isNoPreview && <UploaderAccessOnly />}
                    </>
                  }
                />
              </div>
            );
          })}
        </div>
      ) : (
        <p className="documents__placeholder">No documents available</p>
      )}
      {!isDesktop && <div className="documents-container__line" hidden={IsDropDownOn} />}
    </>
  );
};
